import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type {
  DashboardData,
  ChecklistItemWithEntry,
  ProjectTask,
  SkillItem,
  DailyBanner,
  TimeEntry,
} from '../types';

// Returns today's date string in YYYY-MM-DD (local, America/Denver)
function getTodayString(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
}

export function useDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);

    try {
      const today = getTodayString();

      // ── 1. Fetch active template items (own + shared) ──
      const { data: templates, error: tErr } = await supabase
        .from('intern_checklist_template_items')
        .select('*')
        .eq('active', true)
        .or(`assignee_id.is.null,assignee_id.eq.${user.id}`)
        .order('sort_order', { ascending: true });
      if (tErr) throw tErr;

      // ── 2. Ensure checklist entries exist for today (lazy instantiation) ──
      if (templates && templates.length > 0) {
        const upserts = templates.map((t) =>
          supabase.rpc('upsert_intern_checklist_entry', {
            p_template_item_id: t.id,
            p_entry_date: today,
            p_user_id: user.id,
            p_checked: false,  // only inserts if row doesn't exist
          })
        );
        // Fire all upserts; ignore errors on rows that already exist
        await Promise.allSettled(upserts);
      }

      // ── 3. Fetch today's entries ──
      const { data: entries, error: eErr } = await supabase
        .from('intern_checklist_entries')
        .select('*')
        .eq('entry_date', today)
        .eq('user_id', user.id);
      if (eErr) throw eErr;

      // Join template + entry
      const entryMap = new Map((entries || []).map((e) => [e.template_item_id, e]));
      const checklistItems: ChecklistItemWithEntry[] = (templates || []).map((t) => ({
        ...t,
        entry: entryMap.get(t.id) ?? null,
      }));

      // ── 4. Project tasks (with escalation from view; own + shared) ──
      const { data: tasks, error: taskErr } = await supabase
        .from('intern_project_tasks_view')
        .select('*')
        .neq('status', 'done')  // hide completed tasks from dashboard (they de-emphasize)
        .or(`assignee_id.is.null,assignee_id.eq.${user.id}`)
        .order('due_date', { ascending: true, nullsFirst: false });
      if (taskErr) throw taskErr;

      // Also get recently completed tasks (last 24h) so they de-emphasize at bottom
      const { data: doneTasks } = await supabase
        .from('intern_project_tasks_view')
        .select('*')
        .eq('status', 'done')
        .or(`assignee_id.is.null,assignee_id.eq.${user.id}`)
        .gte('completed_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('completed_at', { ascending: false })
        .limit(5);

      const sortedTasks = sortTasksByUrgency([
        ...(tasks as ProjectTask[] ?? []),
        ...(doneTasks as ProjectTask[] ?? []),
      ]);

      // ── 5. Skill items (own + shared) ──
      const { data: skills, error: skillErr } = await supabase
        .from('intern_skill_items')
        .select('*')
        .neq('status', 'explored')  // hide explored items from dashboard
        .or(`assignee_id.is.null,assignee_id.eq.${user.id}`)
        .order('sort_order', { ascending: true });
      if (skillErr) throw skillErr;

      // ── 6. Today's banner ──
      const { data: bannerRows } = await supabase
        .from('intern_daily_banner')
        .select('*')
        .eq('banner_date', today)
        .limit(1);
      const banner: DailyBanner | null = bannerRows?.[0] ?? null;

      // ── 7. Open timer ──
      const { data: openTimers } = await supabase
        .from('intern_time_entries')
        .select('*')
        .eq('user_id', user.id)
        .is('ended_at', null)
        .limit(1);
      const openTimer: TimeEntry | null = openTimers?.[0] ?? null;

      setData({
        checklistItems,
        projectTasks: sortedTasks,
        skillItems: skills as SkillItem[] ?? [],
        banner,
        openTimer,
      });
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load your day.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Optimistic updates for checklist
  const toggleChecklistItem = useCallback(async (templateItemId: string, checked: boolean) => {
    if (!user?.id) return;
    const today = getTodayString();

    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        checklistItems: prev.checklistItems.map((item) =>
          item.id === templateItemId
            ? {
                ...item,
                entry: item.entry
                  ? { ...item.entry, checked, checked_at: checked ? new Date().toISOString() : null }
                  : null,
              }
            : item
        ),
      };
    });

    await supabase.rpc('upsert_intern_checklist_entry', {
      p_template_item_id: templateItemId,
      p_entry_date: today,
      p_user_id: user.id,
      p_checked: checked,
    });
  }, [user?.id]);

  // Task status update
  const updateTaskStatus = useCallback(async (taskId: string, status: string) => {
    const { error } = await supabase.rpc('set_intern_task_status', {
      p_task_id: taskId,
      p_status: status,
    });
    if (!error) {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          projectTasks: sortTasksByUrgency(
            prev.projectTasks.map((t) =>
              t.id === taskId
                ? {
                    ...t,
                    status: status as ProjectTask['status'],
                    completed_at: status === 'done' ? new Date().toISOString() : t.completed_at,
                  }
                : t
            )
          ),
        };
      });
    }
    return { error };
  }, []);

  // Update skill status/notes
  const updateSkillProgress = useCallback(async (skillId: string, status: string, notes?: string) => {
    const { error } = await supabase.rpc('set_intern_skill_progress', {
      p_skill_id: skillId,
      p_status: status,
      p_notes: notes ?? null,
    });
    if (!error) {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          skillItems: prev.skillItems.map((s) =>
            s.id === skillId
              ? { ...s, status: status as SkillItem['status'], notes: notes ?? s.notes }
              : s
          ),
        };
      });
    }
    return { error };
  }, []);

  // Update open timer in state
  const setOpenTimer = useCallback((timer: TimeEntry | null) => {
    setData((prev) => prev ? { ...prev, openTimer: timer } : prev);
  }, []);

  return {
    data,
    loading,
    error,
    refresh: fetchDashboard,
    toggleChecklistItem,
    updateTaskStatus,
    updateSkillProgress,
    setOpenTimer,
  };
}

// Sort Section B tasks: red/overdue first → orange → nearest due → regular; done at bottom
function sortTasksByUrgency(tasks: ProjectTask[]): ProjectTask[] {
  const order = { red: 0, orange: 1, regular: 2, done: 3 };
  return [...tasks].sort((a, b) => {
    const ea = a.status === 'done' ? 'done' : (a.escalation ?? 'regular');
    const eb = b.status === 'done' ? 'done' : (b.escalation ?? 'regular');
    const diff = (order[ea as keyof typeof order] ?? 2) - (order[eb as keyof typeof order] ?? 2);
    if (diff !== 0) return diff;
    // Same urgency: sort by due_date ascending
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  });
}
